import { getCarHubImageUrl } from '@/entities/car'

import { RANK_VISIBLE_LIMIT } from '../_constants'

import type {
  IRankingList,
  IRankingReferenceDateDto,
  IRankingResponseDto,
  IRankItemDto,
  IRankVehicle,
  TApiRankSort,
  TRankFuelType,
} from '../_types'

/** 미리보기(흐릿) 노출 개수 — 상위 노출 상한 이후 N개 */
const RANK_PREVIEW_COUNT = 2

/** 'YYYY-MM-DD' → 'YYYY년 M월 D일' (파싱 실패 시 원문 반환) */
const formatReferenceDate = (raw: string): string => {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw)
  if (!matched) return raw

  const [, year, month, day] = matched
  return `${year}년 ${Number(month)}월 ${Number(day)}일`
}

/**
 * sort별 기준일 문구를 만든다.
 * - 만족도순/실연비순: 마이클 실사용 데이터 기준일(mycleData)
 * - 유류비순: 오피넷 유가 기준일(oilPrice), 없으면 전기요금 기준일(evPrice)
 */
const resolveReferenceDate = (
  reference: IRankingReferenceDateDto,
  sort: TApiRankSort
): string => {
  const candidate =
    sort === 'FUEL_COST'
      ? (reference.oilPrice ?? reference.evPrice ?? reference.mycleData)
      : (reference.mycleData ?? reference.oilPrice ?? reference.evPrice)

  return candidate ? formatReferenceDate(candidate) : ''
}

/** API 항목 DTO → 도메인 차종 (순위·이미지·유류비 매핑) */
const toVehicle = (item: IRankItemDto): IRankVehicle => ({
  rank: item.rank,
  brandName: item.brand,
  modelName: item.model,
  // TODO(WEB-4527): 백엔드에 모델명(car) 필드 추가 요청함. 수신 시 상세 HeroCard와 동일한
  // [세부모델명, 모델명] 폴백 체인으로 교체:
  //   imageUrls: [getCarHubImageUrl(item.brand, item.model), getCarHubImageUrl(item.brand, item.car)]
  // 현재는 세부모델명 단일이라 세부모델명 파일이 없는 차종(예: BMW M3 (G80)→M시리즈,
  // 캐딜락 XT6)은 이미지가 없어 폴백 일러스트로 노출된다.
  imageUrls: [getCarHubImageUrl(item.brand, item.model)],
  ownerCount: item.ownerCount ?? 0,
  satisfactionScore: item.reviewScore,
  realFuelEfficiency: item.realEfficiency,
  fuelEfficiencyUnit: item.realEfficiencyUnit,
  annualFuelCost: item.annualFuelCost ?? 0,
  fuelType: item.fuelType,
  fuelTypeName: item.fuelTypeName,
})

/**
 * 랭킹 응답 DTO → 도메인 랭킹 리스트.
 * 상위 노출/미리보기 분리는 API가 아닌 클라 몫이라 여기서 slice 한다.
 */
export const mapRankingResponse = (
  dto: IRankingResponseDto,
  { segment, fuelType }: { segment: string; fuelType: TRankFuelType }
): IRankingList => {
  const vehicles = dto.rankingList.map(toVehicle)
  const { oilPrice, evPrice } = dto.referenceDate

  return {
    segment,
    fuelType,
    totalCount: dto.totalCount,
    referenceDate: resolveReferenceDate(dto.referenceDate, dto.sort),
    annualMileage: dto.annualMileage,
    oilPriceDate: oilPrice ? formatReferenceDate(oilPrice) : '',
    evPriceDate: evPrice ? formatReferenceDate(evPrice) : '',
    items: vehicles.slice(0, RANK_VISIBLE_LIMIT),
    previewItems: vehicles.slice(
      RANK_VISIBLE_LIMIT,
      RANK_VISIBLE_LIMIT + RANK_PREVIEW_COUNT
    ),
    // 인앱 웹뷰는 slice 없이 전체를 노출한다 (판별은 클라 몫이라 전체 리스트를 함께 내려준다)
    allItems: vehicles,
  }
}
